import { useCallback, useEffect, useRef, useState } from 'react';
import { ScreenPresentation, type PresentationLayout } from '../../../../packages/browser-media/src/screen-presentation';
import { PresenterAudio } from '../../../../packages/browser-media/src/presenter-audio';
import { getIceServers } from '../lib/ice';

const WHIP_ENDPOINT = `${window.location.origin}/rtc/v1/whip/`;

export type PublishState = 'idle' | 'preview' | 'connecting' | 'live' | 'error';

export interface WhipPublisher {
  state: PublishState;
  error: string;
  stats: { kbps: number; resolution: string; rtt: number | null };
  /** Attach to the <video> that shows the local camera. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** The camera is on (so a failed "Go live" can retry without asking again). */
  hasCamera: boolean;
  previewStream: MediaStream | null;
  startPreview: () => Promise<void>;
  goLive: () => Promise<void>;
  stop: () => void;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  toggleCamera: () => void;
  toggleMicrophone: () => void;
  devices: MediaDeviceInfo[];
  selectedCamera: string;
  selectedMicrophone: string;
  devicePending: boolean;
  deviceError: string;
  chooseDevice: (kind: 'video' | 'audio', id: string) => Promise<void>;
  microphoneLevel: number;
  audioSuspended: boolean;
  resumeAudio: () => Promise<void>;
  includeScreenAudio: boolean;
  setIncludeScreenAudio: (value: boolean) => void;
  screenAudioAvailable: boolean;
  screenVolume: number;
  setScreenVolume: (value: number) => void;
  sharing: boolean;
  sharePending: boolean;
  shareError: string;
  shareSupported: boolean;
  shareLayout: PresentationLayout;
  startSharing: () => Promise<void>;
  stopSharing: () => Promise<void>;
  setShareLayout: (layout: PresentationLayout) => void;
}

/**
 * WHIP (WebRTC-HTTP Ingestion Protocol) publishing from the browser, as a hook.
 *
 * It used to be a self-contained card with its own buttons. The stream page's
 * header now owns the one "Go live / End stream" button, so the camera, the
 * connection and the controls have to be separable — hence a hook the page
 * drives, with the preview rendered wherever the page wants it.
 *
 * Captures the camera + mic and pushes via WebRTC to SRS, which bridges it to
 * RTMP internally (rtc_to_rtmp), so the same broadcast plays over HLS too.
 *
 *   1. getUserMedia → MediaStream
 *   2. RTCPeerConnection, addTrack for audio + video (H.264 preferred)
 *   3. createOffer, wait for ICE gathering
 *   4. POST the SDP to /rtc/v1/whip/?app=live&stream=<key>
 *   5. setRemoteDescription with the answer; media flows over UDP 8000
 */
export function useWhipPublisher(streamKey: string): WhipPublisher {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const outgoingRef = useRef<MediaStream | null>(null);
  const audioOutput = useRef<PresenterAudio | null>(null);
  const deviceEpoch = useRef(0), deviceBusy = useRef(false);
  const micEnabled = useRef(true), camEnabled = useRef(true);
  const [cameraEnabled, setCameraEnabled] = useState(true), [microphoneEnabled, setMicrophoneEnabled] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState(''), [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [devicePending, setDevicePending] = useState(false), [deviceError, setDeviceError] = useState('');
  const [microphoneLevel, setMicrophoneLevel] = useState(0), [audioSuspended, setAudioSuspended] = useState(false);
  const [includeScreenAudio, setIncludeScreenAudio] = useState(false), [screenAudioAvailable, setScreenAudioAvailable] = useState(false);
  const [screenVolume, setScreenVolumeState] = useState(75);
  const cameraOutput = useRef<ScreenPresentation | null>(null);
  const captureRef = useRef<{ display: MediaStream; mixer: ScreenPresentation } | null>(null);
  const captureEpoch = useRef(0);
  const shareBusy = useRef(false);
  const [sharing, setSharing] = useState(false);
  const [sharePending, setSharePending] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareLayout, setShareLayoutState] = useState<PresentationLayout>('screen');
  const outputStream = () => captureRef.current?.mixer.stream ?? cameraOutput.current?.stream ?? streamRef.current;
  const disposeShare = () => {
    captureEpoch.current++;
    const capture = captureRef.current; captureRef.current = null;
    audioOutput.current?.setScreen(null); setScreenAudioAvailable(false);
    capture?.mixer.close(); capture?.display.getTracks().forEach(t => t.stop());
  };
  const stopSharing = async () => {
    const capture = captureRef.current;
    audioOutput.current?.setScreen(null); setScreenAudioAvailable(false);
    captureEpoch.current++;
    const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
    try {
      const camera = cameraOutput.current?.stream.getVideoTracks()[0];
      if (sender && camera) await sender.replaceTrack(camera);
      if (captureRef.current !== capture) return;
      disposeShare(); setSharing(false); setShareError('');
    } catch { setShareError('Could not return to the camera. Retry Stop sharing or end the broadcast.'); }
  };
  const startSharing = async () => {
    if (!streamRef.current || captureRef.current || shareBusy.current || deviceBusy.current || state === 'connecting') return;
    if (!navigator.mediaDevices?.getDisplayMedia) { setShareError('Screen sharing requires a supported desktop browser.'); return; }
    const attempt = ++captureEpoch.current;
    shareBusy.current = true; setSharePending(true); setShareError('');
    let display: MediaStream | null = null, mixer: ScreenPresentation | null = null;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 25 }, audio: includeScreenAudio });
      if (attempt !== captureEpoch.current || !streamRef.current) { display.getTracks().forEach(t => t.stop()); return; }
      if (!includeScreenAudio) display.getAudioTracks().forEach(t => t.stop());
      const track = display.getVideoTracks()[0];
      if (!track || track.readyState !== 'live') throw Error('No screen was selected. Try again.');
      mixer = new ScreenPresentation(display); mixer.setCamera(streamRef.current);
      const capture = { display, mixer };
      audioOutput.current?.setScreen(display);
      const sender = pcRef.current?.getSenders().find(s => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(mixer.stream.getVideoTracks()[0]);
      if (attempt !== captureEpoch.current || !streamRef.current) { mixer.close(); display.getTracks().forEach(t => t.stop()); return; }
      captureRef.current = capture;
      const sound = display.getAudioTracks().find(t => t.readyState === 'live');
      setScreenAudioAvailable(!!sound);
      sound?.addEventListener('ended', () => { if (captureRef.current === capture) { audioOutput.current?.setScreen(null); setScreenAudioAvailable(false); } }, { once: true });
      track.addEventListener('ended', () => { if (captureRef.current === capture) void stopSharing(); }, { once: true });
      setShareLayoutState('screen'); setSharing(true);
      if ((track as MediaStreamTrack).readyState === 'ended') void stopSharing();
    } catch (e: any) {
      mixer?.close(); display?.getTracks().forEach(t => t.stop());
      if (attempt === captureEpoch.current) audioOutput.current?.setScreen(null);
      if (attempt === captureEpoch.current) setShareError(e?.name === 'NotAllowedError'
        ? 'Screen sharing was cancelled or permission was denied. Your camera and microphone are unchanged.'
        : e?.message ?? 'Could not share your screen.');
    } finally { shareBusy.current = false; setSharePending(false); }
  };
  const setShareLayout = (layout: PresentationLayout) => { captureRef.current?.mixer.setLayout(layout); setShareLayoutState(layout); };
  const statsTimer = useRef<number | null>(null);
  const lastBytes = useRef(0);
  const epoch = useRef(0);
  const recoveryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<PublishState>('idle');
  const [error, setError] = useState('');
  const [stats, setStats] = useState<{ kbps: number; resolution: string; rtt: number | null }>({ kbps: 0, resolution: '—', rtt: null });

  const refreshDevices = async () => {
    const current = deviceEpoch.current;
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      if (current === deviceEpoch.current && streamRef.current) setDevices(list.filter(d => ['videoinput', 'audioinput'].includes(d.kind)));
    } catch { if (streamRef.current) setDeviceError('Could not list devices. Your current devices remain active.'); }
  };
  const watchDevice = (track: MediaStreamTrack) => {
    track.addEventListener('ended', () => {
      if (!streamRef.current?.getTracks().includes(track)) return;
      if (track.kind === 'audio') { micEnabled.current = false; setMicrophoneEnabled(false); audioOutput.current?.setMicrophoneEnabled(false); }
      else { camEnabled.current = false; setCameraEnabled(false); cameraOutput.current?.setSource(null); }
      setDeviceError(`${track.kind === 'audio' ? 'Microphone' : 'Camera'} disconnected. Choose an available device in Devices & audio.`);
      void refreshDevices();
    }, { once: true });
  };
  const toggleCamera = () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || track.readyState !== 'live') { setDeviceError('Choose an available camera in Devices & audio.'); return; }
    camEnabled.current = !camEnabled.current; track.enabled = camEnabled.current; setCameraEnabled(camEnabled.current);
    cameraOutput.current?.setSource(camEnabled.current ? new MediaStream([track]) : null);
  };
  const toggleMicrophone = () => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (!track || track.readyState !== 'live') { setDeviceError('Choose an available microphone in Devices & audio.'); return; }
    micEnabled.current = !micEnabled.current; track.enabled = micEnabled.current;
    audioOutput.current?.setMicrophoneEnabled(micEnabled.current); setMicrophoneEnabled(micEnabled.current);
  };
  const setScreenVolume = (value: number) => { audioOutput.current?.setScreenVolume(value / 100); setScreenVolumeState(value); };
  const resumeAudio = async () => { try { await audioOutput.current?.resume(); } catch { setDeviceError('Could not resume audio. Check browser audio permissions.'); } };
  const chooseDevice = async (kind: 'video' | 'audio', id: string) => {
    if (!streamRef.current || deviceBusy.current || shareBusy.current || state === 'connecting') return;
    const attempt = ++deviceEpoch.current;
    deviceBusy.current = true; setDevicePending(true); setDeviceError('');
    let capture: MediaStream | null = null;
    try {
      capture = await navigator.mediaDevices.getUserMedia(kind === 'video'
        ? { video: { deviceId: id ? { exact: id } : undefined, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false }
        : { video: false, audio: { deviceId: id ? { exact: id } : undefined, echoCancellation: true, noiseSuppression: true } });
      if (attempt !== deviceEpoch.current || !streamRef.current) { capture.getTracks().forEach(t => t.stop()); return; }
      const next = capture.getTracks().find(t => t.kind === kind && t.readyState === 'live');
      if (!next) throw Error('The selected device is unavailable.');
      capture.getTracks().filter(t => t !== next).forEach(t => t.stop());
      next.enabled = kind === 'audio' ? micEnabled.current : camEnabled.current;
      if (kind === 'audio') audioOutput.current!.setMicrophone(new MediaStream([next]));
      const previous = streamRef.current.getTracks().filter(t => t.kind === kind);
      previous.forEach(t => streamRef.current!.removeTrack(t)); streamRef.current.addTrack(next);
      if (kind === 'video') {
        cameraOutput.current?.setSource(camEnabled.current ? new MediaStream([next]) : null);
        captureRef.current?.mixer.setCamera(streamRef.current);
        setSelectedCamera(next.getSettings().deviceId ?? id);
      } else setSelectedMicrophone(next.getSettings().deviceId ?? id);
      watchDevice(next); previous.forEach(t => t.stop()); void refreshDevices();
    } catch (e: any) {
      capture?.getTracks().forEach(t => t.stop());
      if (attempt === deviceEpoch.current) setDeviceError(`Could not switch ${kind === 'audio' ? 'microphone' : 'camera'}. Your previous device is unchanged. ${e?.name === 'NotAllowedError' ? 'Allow device access in your browser.' : e?.message ?? ''}`);
    } finally { if (attempt === deviceEpoch.current) { deviceBusy.current = false; setDevicePending(false); } }
  };
  useEffect(() => {
    const changed = () => { if (streamRef.current) void refreshDevices(); };
    navigator.mediaDevices?.addEventListener('devicechange', changed);
    const timer = setInterval(() => { setMicrophoneLevel(audioOutput.current?.microphoneLevel() ?? 0); setAudioSuspended(!!audioOutput.current && audioOutput.current.context.state !== 'running'); }, 150);
    return () => { clearInterval(timer); deviceEpoch.current++; navigator.mediaDevices?.removeEventListener('devicechange', changed); };
  }, []);

  // The preview <video> may mount after the camera starts (it lives in a
  // different part of the page than the button), so re-attach on every render.
  useEffect(() => {
    if (videoRef.current && outputStream() && videoRef.current.srcObject !== outputStream()) {
      videoRef.current.srcObject = outputStream();
    }
  });

  const startPreview = useCallback(async () => {
    if (pcRef.current || streamRef.current) return;
    const attempt = ++epoch.current;
    setError('');
    let sound: PresenterAudio | null = null;
    try {
      sound = new PresenterAudio(); audioOutput.current = sound; await sound.resume();
      if (attempt !== epoch.current) { sound.close(); return; }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      if (attempt !== epoch.current) { stream.getTracks().forEach(t => t.stop()); sound.close(); return; }
      // A fixed canvas keeps camera/screen switches at one encoded size for MP4 recording.
      try {
        const canvas = new ScreenPresentation(stream); canvas.setLayout('screen-only');
        cameraOutput.current = canvas; sound.setMicrophone(stream);
      } catch (error) { stream.getTracks().forEach(t => t.stop()); throw error; }
      streamRef.current = stream;
      micEnabled.current = true; camEnabled.current = true; setMicrophoneEnabled(true); setCameraEnabled(true);
      sound.setMicrophoneEnabled(true); sound.setScreenVolume(.75); setScreenVolumeState(75);
      stream.getTracks().forEach(watchDevice);
      setSelectedCamera(stream.getVideoTracks()[0]?.getSettings().deviceId ?? ''); setSelectedMicrophone(stream.getAudioTracks()[0]?.getSettings().deviceId ?? '');
      setDeviceError(''); void refreshDevices();
      if (videoRef.current) videoRef.current.srcObject = stream;
      setStats((s) => ({ ...s, resolution: '720p' }));
      setState('preview');
    } catch (e: any) {
      sound?.close();
      if (attempt !== epoch.current) return;
      audioOutput.current = null; cameraOutput.current?.close(); cameraOutput.current = null;
      setError(
        e?.name === 'NotAllowedError'
          ? 'The browser blocked the camera or microphone. Allow access in the address bar, then try again.'
          : e?.name === 'NotFoundError'
            ? 'No camera or microphone was found on this device.'
            : e?.message ?? 'Could not start the camera.',
      );
      setState('error');
    }
  }, []);

  const stop = useCallback(() => {
    epoch.current++; deviceEpoch.current++; deviceBusy.current = false; setDevicePending(false);
    setDeviceError(''); setMicrophoneLevel(0); setAudioSuspended(false);
    disposeShare(); setSharing(false); setShareError('');
    if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
    if (statsTimer.current) { clearInterval(statsTimer.current); statsTimer.current = null; }
    lastBytes.current = 0;
    pcRef.current?.close();
    pcRef.current = null; outgoingRef.current = null;
    cameraOutput.current?.close(); cameraOutput.current = null;
    audioOutput.current?.close(); audioOutput.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setState('idle');
    setError('');
    setStats({ kbps: 0, resolution: '—', rtt: null });
  }, []);

  const goLive = useCallback(async () => {
    if (!streamRef.current || pcRef.current || shareBusy.current || deviceBusy.current) return;
    const attempt = ++epoch.current;
    setState('connecting');
    setError('');
    try {
      const iceServers = await getIceServers(streamKey);
      if (attempt !== epoch.current || !streamRef.current) return;
      const pc = new RTCPeerConnection({ iceServers, bundlePolicy: 'max-bundle' });
      pcRef.current = pc;
      const output = outgoingRef.current = new MediaStream([...outputStream()!.getVideoTracks(), ...audioOutput.current!.stream.getAudioTracks()]);
      // Negotiate audio first, matching the established viewer path across browsers.
      [...output.getAudioTracks(), ...output.getVideoTracks()].forEach((t) => pc.addTrack(t, output));

      // Prefer H.264 so SRS can bridge to RTMP/HLS without transcoding.
      for (const tx of pc.getTransceivers().filter((t) => t.sender.track?.kind === 'video')) {
        const caps = RTCRtpSender.getCapabilities('video');
        if (!caps) continue;
        const h264 = caps.codecs.filter((c) => /H264/i.test(c.mimeType));
        const others = caps.codecs.filter((c) => !/H264/i.test(c.mimeType));
        if (h264.length && typeof tx.setCodecPreferences === 'function') {
          try { tx.setCodecPreferences([...h264, ...others]); } catch { /* Safari */ }
        }
      }

      for (const tx of pc.getTransceivers().filter(t => t.sender.track?.kind === 'audio')) {
        const opus = RTCRtpSender.getCapabilities('audio')?.codecs.filter(c => /audio\/opus/i.test(c.mimeType)) ?? [];
        if (opus.length && tx.setCodecPreferences) tx.setCodecPreferences(opus);
      }

      const failConnection = () => {
        if (pcRef.current !== pc) return;
        pcRef.current = null; pc.close();
        if (statsTimer.current) clearInterval(statsTimer.current);
        if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
        setState('error');
        setError('The broadcast connection stopped. Your camera is ready; check your connection and try Go live again.');
      };
      recoveryTimer.current = setTimeout(failConnection, 25000);
      pc.addEventListener('connectionstatechange', () => {
        if (pcRef.current !== pc) return;
        if (pc.connectionState === 'connected') { if (recoveryTimer.current) clearTimeout(recoveryTimer.current); setState('live'); setError(''); }
        if (pc.connectionState === 'failed') failConnection();
        if (pc.connectionState === 'disconnected') {
          setState('connecting'); setError('Connection interrupted — waiting to reconnect…');
          if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
          recoveryTimer.current = setTimeout(failConnection, 15000);
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      for (const sender of pc.getSenders().filter(s => s.track?.kind === 'video')) {
        const params = sender.getParameters(); params.degradationPreference = 'maintain-resolution';
        for (const encoding of params.encodings) { encoding.scaleResolutionDownBy = 1; encoding.maxBitrate = 2500000; encoding.maxFramerate = 25; }
        try { await sender.setParameters(params); } catch { /* Older browsers may not support this hint. */ }
      }
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') return resolve();
        const t = setTimeout(resolve, 5000);
        pc.addEventListener('icegatheringstatechange', () => {
          if (pc.iceGatheringState === 'complete') { clearTimeout(t); resolve(); }
        });
      });

      if (attempt !== epoch.current || pcRef.current !== pc) return;
      const r = await fetch(`${WHIP_ENDPOINT}?app=live&stream=${encodeURIComponent(streamKey)}`, {
        method: 'POST',
        signal: AbortSignal.timeout(15000),
        headers: { 'Content-Type': 'application/sdp' },
        body: pc.localDescription?.sdp ?? '',
      });
      if (!r.ok) {
        const detail = await r.text().catch(() => '');
        throw new Error(
          r.status === 400
            ? `The media server refused the broadcast. ${detail.slice(0, 160)}`
            : `Could not reach the media server (${r.status}).`,
        );
      }
      const answer = await r.text();
      if (attempt !== epoch.current || pcRef.current !== pc) return;
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      if (attempt !== epoch.current || pcRef.current !== pc) return;

      statsTimer.current = window.setInterval(async () => {
        const cur = pcRef.current;
        if (!cur || cur.connectionState === 'closed') return;
        try {
          const reports = await cur.getStats();
          let bytes = 0;
          let rtt: number | null = null;
          reports.forEach((rep: any) => {
            if (rep.type === 'outbound-rtp' && rep.kind === 'video') bytes += rep.bytesSent ?? 0;
            if (rep.type === 'remote-inbound-rtp' && rep.kind === 'video' && rep.roundTripTime) rtt = Math.round(rep.roundTripTime * 1000);
          });
          const prev = lastBytes.current;
          lastBytes.current = bytes;
          setStats((s) => ({ ...s, kbps: prev ? Math.max(0, Math.round((bytes - prev) * 8 / 1000)) : 0, rtt }));
        } catch { /* stats are cosmetic */ }
      }, 1000);
    } catch (e: any) {
      if (attempt !== epoch.current) return;
      if (recoveryTimer.current) clearTimeout(recoveryTimer.current);
      setError(e?.message ?? 'Could not start the broadcast.');
      setState('error');
      pcRef.current?.close();
      pcRef.current = null;
    }
  }, [streamKey]);

  useEffect(() => () => stop(), [stop]);

  return { state, error, stats, videoRef, previewStream: outputStream(), cameraEnabled, microphoneEnabled, toggleCamera, toggleMicrophone, devices, selectedCamera, selectedMicrophone, devicePending, deviceError, chooseDevice, microphoneLevel, audioSuspended, resumeAudio, includeScreenAudio, setIncludeScreenAudio, screenAudioAvailable, screenVolume, setScreenVolume, sharing, sharePending, shareError, shareSupported: !!navigator.mediaDevices?.getDisplayMedia, shareLayout, startSharing, stopSharing, setShareLayout, hasCamera: !!streamRef.current, startPreview, goLive, stop };
}
